import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetMaterialsQuery } from '../services/materialsApi';
import styles from '../css/HeroCarousel.module.css';
import BookmarkButton from './BookmarkButton';

const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prevSlide, setPrevSlide] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [bookmarks, setBookmarks] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const animationTimer = useRef(null);
  const navigate = useNavigate();

  const { data: heroData } = useGetMaterialsQuery({
    sort_by: 'top100',
    sort_order: 'desc',
    per_page: 20,
  });

  const heroSlides = useMemo(() => {
    const items = heroData?.data ?? [];
    const shuffled = [...items].sort(() => Math.random() - 0.5);
    return shuffled.map((item) => ({
      id: item.id,
      image: `https://doramahype.ru/uploads/posters/${item.poster_url}`,
      title: item.title,
      rating: item.imdb_rating || item.mydramalist_rating || item.kinopoisk_rating,
      genres: item.genres ?? [],
      description: item.description || item.synopsis || '',
      link: item.slug && item.slug_id ? `/dorama/${item.slug}--${item.slug_id}` : `/dorama/${item.id}`,
      bookmark: item.user_meta?.bookmark || null,
    }));
  }, [heroData]);

  const goToSlide = (index) => {
    if (isAnimating || index === currentSlide || heroSlides.length === 0) return;
    setIsAnimating(true);
    setPrevSlide(currentSlide);
    setCurrentSlide(index);
    clearTimeout(animationTimer.current);
    animationTimer.current = setTimeout(() => {
      setPrevSlide(null);
      setIsAnimating(false);
    }, 1200);
  };

  useEffect(() => {
    if (heroSlides.length === 0 || dropdownOpen) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const next = (prev + 1) % heroSlides.length;
        setPrevSlide(prev);
        setIsAnimating(true);
        clearTimeout(animationTimer.current);
        animationTimer.current = setTimeout(() => {
          setPrevSlide(null);
          setIsAnimating(false);
        }, 900);
        return next;
      });
    }, 10000);
    return () => clearInterval(interval);
  }, [heroSlides.length, dropdownOpen]);

  useEffect(() => {
    return () => clearTimeout(animationTimer.current);
  }, []);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) {
      goToSlide((currentSlide + 1) % heroSlides.length);
    } else if (distance < -minSwipeDistance) {
      goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
    }
  };

  if (heroSlides.length === 0) return <div className={styles.heroSkeleton} aria-hidden="true" />;

  return (
    <div
      className={styles.heroCarousel}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {heroSlides.map((slide, index) => {
        const isActive = index === currentSlide;
        const isLeaving = index === prevSlide;
        if (!isActive && !isLeaving) return null;

        return (
          <div
            key={slide.id}
            className={`${styles.carouselSlide} ${isActive ? styles.slideEnter : styles.slideLeave}`}
            style={isActive && dropdownOpen ? { zIndex: 30 } : undefined}
          >
            {/* Обёртка с overflow:hidden только для цветного фона */}
            <div className={styles.slideColorBgWrap}>
              <img
                src={slide.image}
                alt=""
                aria-hidden="true"
                className={styles.slideColorBg}
              />
            </div>
            <div className={styles.slideOverlay}>
              <div className={styles.innerContent}>
                <div className={`${styles.slideContent} ${isActive ? styles.contentEnter : ''}`}>
                  {slide.rating && (
                    <div className={styles.slideBadges}>
                      <span className={`${styles.badge} ${styles.badgeRating}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        {slide.rating}
                      </span>
                    </div>
                  )}

                  <h1 className={styles.slideTitle}>{slide.title}</h1>

                  <div className={styles.slideGenres}>
                    {slide.genres.map((genre, idx) => (
                      <span key={idx} className={styles.genre}>{genre}</span>
                    ))}
                  </div>

                  {slide.description && (
                    <p className={styles.slideDescription}>{slide.description}</p>
                  )}

                  <div className={styles.slideActions}>
                    <button className={styles.btnWatch} onClick={() => navigate(slide.link)}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                      Смотреть
                    </button>
                    <BookmarkButton
                      materialId={slide.id}
                      initialBookmark={bookmarks[slide.id] !== undefined ? bookmarks[slide.id] : slide.bookmark}
                      variant="hero"
                      defaultListKey="plan_to_watch"
                      onBookmarkChange={(bm) => setBookmarks((prev) => ({ ...prev, [slide.id]: bm }))}
                      onDropdownChange={setDropdownOpen}
                    />
                  </div>
                </div>

                <div className={`${styles.slidePosterWrap} ${isActive ? styles.posterEnter : ''}`}>
                  <img src={slide.image} alt={slide.title} className={styles.slidePoster} />
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className={styles.carouselDots}>
        {heroSlides.map((_, index) => (
          <button
            key={index}
            className={`${styles.dot} ${index === currentSlide ? styles.active : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Слайд ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroCarousel;
